import React, { useState, useEffect } from 'react';
import { css } from '@emotion/core';
import { Select, Space, Modal, Typography } from 'antd';
import { useHistory } from 'react-router-dom';

import { ApiDetailsCss, ErrorModalCss, SortStyle } from './ApiDetailList.style';
import { API_DEVELOPMENT_STATUS } from './../../common/constants/AppConstants';
import { FilterMapping, FilterPersonas } from './../../common/constants/AppConstants';
import { getPublicSiteImageSrc } from 'Client/common/utils/AppUtils';
import { Button } from 'react-scroll';
import { CloseOutlined } from '@ant-design/icons';
import ProgressBar from 'Client/components/ProgressBar/ProgressBar';

const { Option } = Select;
const getDropdownStyles = showOutline => css`
 ${!showOutline
    ? `
    .ant-select-selection-search-input:focus {
      outline: none !important;
    }
    .ant-select-selector:focus {
      outline: none !important;
    }
  `
    : `
    .ant-select-selection-search-input:focus {
      outline: 2px solid #0079c2 !important;
      outline-offset: 2px !important;
      opacity: 1 !important;
    }
    .ant-select-selector:focus {
      outline: 2px solid #0079c2 !important;
      outline-offset: 2px !important;
      opacity: 1 !important;
    }
  `}`

const ApiDetailList = ({ apiList, groupedCheckedValues, setGroupedCheckedValues, setCheckedValues }) => {
  const [sortType, setSortType] = useState('A-Z'); // State for sort type
  const [isModalVisible, setIsModalVisible] = useState(false); // State for modal visibility
  const [isSelectOpen, setIsSelectOpen] = useState(false); // State for select dropdown
  const history = useHistory();
  const [showOutline, setShowOutline] = React.useState(false);

  const handleSortChange = value => {
    setSortType(value);
  };

  const makeLink = (name, apiName) => {
    var contactLink = document.createElement('a'); //create <a> tag
    contactLink.setAttribute('id', `read-more-${apiName.replace(/\s+/g, '-').toLowerCase()}`); //set unique id
    contactLink.setAttribute('role', 'button'); //set tabindex att for <a> tag
    contactLink.setAttribute('tabIndex', '0'); //set tabindex att for <a> tag
    contactLink.setAttribute('aria-label', `Read more about ${apiName}`); //set unique aria-label
    contactLink.classList.add('readmore-text'); // add class to <a> tag
    var contactLinkTxt = document.createTextNode(name); //new text node
    contactLink.appendChild(contactLinkTxt); //append text as child of <a> tag
    return contactLink;
  };

  const makeMessage = item => {
    const word = ' Read More';
    var highRiskmsg = item.apiDescription;
    if (highRiskmsg.includes(word)) {
      return item.apiDescription;
    } else {
      var span = document.createElement('span');
      span.innerHTML = highRiskmsg;
      span.appendChild(makeLink(word, item?.apiName));
      return span.outerHTML;
    }
  };

  const filteredApiList =
    Object.keys(groupedCheckedValues).length === 0
      ? apiList?.data
      : apiList?.data.filter(api => {
          return Object.entries(groupedCheckedValues).every(([category, values]) => {
            const apiField = FilterMapping[category];
            if (apiField === 'persona') {
              if (values.some(value => value.toLowerCase() === 'all')) {
                return Object.keys(FilterPersonas).some(persona => api[apiField].includes(FilterPersonas[persona]));
              }
              return values.some(value => api[apiField].includes(FilterPersonas[value]));
            }
            if (category === 'POPULAR APIs') {
              return values.includes(api[apiField]);
            }
            return values?.some(value => api[apiField]?.includes(value));
          });
        });

  const sortedApiList = [...filteredApiList].sort((a, b) => {
    if (sortType === 'A-Z') {
      return a.apiName.localeCompare(b.apiName);
    } else if (sortType === 'Z-A') {
      return b.apiName.localeCompare(a.apiName);
    }
    return 0;
  });

  const generatedApiList = [...sortedApiList].map(item => {
    item.apiDescription = makeMessage(item);
    return item;
  });

  const onclickReadMore = text => {
    history.push(`/explore/apiDetails/${text}`);
  };

  useEffect(() => {
    if (generatedApiList.length === 0) {
      setIsModalVisible(true);
    }
  }, [generatedApiList]);

  const handleOk = () => {
    setIsModalVisible(false);
    setGroupedCheckedValues({});
    setCheckedValues([]);
  };

  // Workaround: Remove invalid aria-expanded attribute from Ant Design Select
  useEffect(() => {
    const removeAriaExpanded = () => {
      const sortSelectElements = document.querySelectorAll('.sort-input.ant-select, .ant-select.sort-input');
      sortSelectElements.forEach(element => {
        if (element && element.hasAttribute('aria-expanded')) {
          element.removeAttribute('aria-expanded');
        }
      });
    };

    const fixAriaActivedescendant = () => {
      const rcSelectElement = document.querySelector('#rc_select_0');
      if (rcSelectElement && rcSelectElement.hasAttribute('aria-activedescendant')) {
        const currentValue = rcSelectElement.getAttribute('aria-activedescendant');
        // Check if the referenced element exists, if not remove the attribute
        const referencedElement = document.getElementById(currentValue);
        if (!referencedElement) {
          rcSelectElement.removeAttribute('aria-activedescendant');
        }
      }
    };

    // Multiple attempts to remove the attributes
    const attemptRemoval = () => {
      removeAriaExpanded();
      fixAriaActivedescendant();
      setTimeout(() => {
        removeAriaExpanded();
        fixAriaActivedescendant();
      }, 100);
      setTimeout(() => {
        removeAriaExpanded();
        fixAriaActivedescendant();
      }, 500);
      setTimeout(() => {
        removeAriaExpanded();
        fixAriaActivedescendant();
      }, 1000);
    };

    // Initial removal
    attemptRemoval();
    
    // Set up observer for continuous monitoring
    const observer = new MutationObserver(() => {
      removeAriaExpanded();
      fixAriaActivedescendant();
    });
    
    // Observe the entire document for changes
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['aria-expanded', 'aria-activedescendant'],
      subtree: true,
      childList: true
    });
    
    return () => {
      observer.disconnect();
    };
  }, [sortType, isSelectOpen]); // Re-run when these states change
  useEffect(() => {
    const handleGlobalKeyDown = e => {
      if (e.key === 'Tab') {
        setShowOutline(true);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);
  const handleMouseDown = () => {
    setShowOutline(false);
  };

  return (
    apiList?.status === 'pending' ? 
    <ProgressBar blueColor />
    :
    <>
      <div css={SortStyle}>
        <div className="showingAllStyle">
          <span>
            <Typography.Title className="mb-0" level={5}><b>Showing all {sortedApiList.length} APIs </b></Typography.Title>
          </span>
        </div>
        <div className="sortContainerStyle">
          <span className="sort-section">
            <Space>
              <label htmlFor="sort-select">
                <span className="sort-label">Sort By </span>
              </label>
              <Select 
                id="sort-select"
                tabIndex={0}
                defaultValue="A-Z"
                className="sort-input"
                css={getDropdownStyles(showOutline)}
                onChange={handleSortChange}
                onDropdownVisibleChange={open => setIsSelectOpen(open)}
                suffixIcon={<img src={getPublicSiteImageSrc('select_suffix_icon.svg')} alt="select_suffix_icon" aria-hidden="true" />}
                aria-labelledby="sort-label"
                aria-expanded={isSelectOpen}
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownMatchSelectWidth={false}
                virtual={false}
                dropdownStyle={{ position: 'absolute' }}
                dropdownRender={(menu) => <div>{menu}</div>}
                listHeight={200}
                showSearch={false}
                aria-label="Sort APIs by name"
                onClick={handleMouseDown}
              >
                <Option value="A-Z">API Name (A-Z)</Option>
                <Option value="Z-A">API Name (Z-A)</Option>
              </Select>
            </Space>
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }} css={ApiDetailsCss}>
        {(generatedApiList.length === 0 && apiList?.status === "success") ? (
          <Modal 
            closable={false} 
            visible={isModalVisible} 
            onOk={handleOk} 
            footer={null} 
            centered 
            css={ErrorModalCss}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', fontFamily: "Elevance Sans"}}>
            <CloseOutlined
                style={{ position: 'absolute', top: '16px', right: '16px', cursor: 'pointer',  color: '#376FEC', fontSize: '20px' }}
                onClick={handleOk}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOk();
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label="Close modal"
              />
              <img
                src={require(`./../../common/img/new-public-Ui/status_icon.svg`).default}
                alt="Status Icon"
                style={{ marginBottom: '16px' }}
                aria-hidden="true"
              />
              <h3 id="modal-title" style={{fontWeight:600, fontSize: 24}}>No Matching API's Found</h3>
              <p id="modal-description" style={{fontWeight:400, fontSize: 18}}>Consider adjusting your filters to find suitable API's</p>
            </div>
          </Modal>
        ) : (
          generatedApiList.map(
            (api, index) =>
              api.apiStatus !== API_DEVELOPMENT_STATUS && (
                <div key={index} className="apiListItem" role="article" >
                  <img
                    src={require(`./../../common/img/new-public-Ui/api-images-list/${api?.apiId}.svg`).default}
                    alt={`${api.apiName} logo`}
                    className="apiImage"
                    aria-hidden="true"
                  />
                  <div className="apiListCard">
                    <h3 className="apiHeading" role="heading" aria-level="3" id={`api-name-${api.apiName.replace(/\s+/g, '-').toLowerCase()}`}>{api.apiName}</h3>
                    <div className="messageContainer">
                      <p
                        className="apiDesc"
                        dangerouslySetInnerHTML={{ __html: api.apiDescription }}
                        onClick={() => onclickReadMore(api.apiName)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onclickReadMore(api.apiName);
                          }
                        }}
                        aria-label={`Read more about ${api.apiName} API`}
                      ></p>
                    </div>
                  </div>
                </div>
              ),
          )
        )}
      </div>
    </>
  );
};

export default ApiDetailList;
